// IFC-class → icon + color lookup.
// The `icon` is a key into IFC_ICON_SVGS (see IfcIconSvgs.ts), not a Font Awesome class.
// The <IfcIcon> Vue component renders the matching SVG.

export interface IfcClassVisual {
    icon: string; // Key in IFC_ICON_SVGS (e.g. "wall", "door", "pipe")
    color: string; // Hex color used to tint the icon via currentColor
}

const SPATIAL_COLOR = '#5b9bd5';
const STRUCTURAL_COLOR = '#ef5350';
const OPENING_COLOR = '#66bb6a';
const FLAT_COLOR = '#9e9e9e';
const WALL_COLOR = '#8d6e63';
const MEP_COLOR = '#29b6f6';
const ASSEMBLY_COLOR = '#ab47bc';
const CIRCULATION_COLOR = '#DAA520';
const FINISH_COLOR = '#8D6E63';
const ELECTRIC_COLOR = '#FDD835';
const DEFAULT_COLOR = '#78909c';

/**
 * Resolves display visuals for an IFC class name.
 * Case-insensitive substring matching — catches IfcWall, IfcWallStandardCase, etc.
 */
export function getIfcClassVisual(ifcClass: string | null | undefined): IfcClassVisual {
    const c = (ifcClass ?? '').toLowerCase();

    // Spatial containers
    if (c.includes('project')) return { icon: 'project', color: SPATIAL_COLOR };
    if (c.includes('site')) return { icon: 'site', color: SPATIAL_COLOR };
    if (c.includes('building') && !c.includes('storey') && !c.includes('element'))
        return { icon: 'building', color: SPATIAL_COLOR };
    if (c.includes('storey')) return { icon: 'storey', color: SPATIAL_COLOR };
    if (c.includes('space')) return { icon: 'space', color: SPATIAL_COLOR };
    if (c.includes('zone')) return { icon: 'zone', color: SPATIAL_COLOR };

    // Openings
    if (c.includes('door')) return { icon: 'door', color: OPENING_COLOR };
    if (c.includes('window')) return { icon: 'window', color: OPENING_COLOR };
    if (c.includes('opening')) return { icon: 'opening', color: OPENING_COLOR };

    // Walls
    if (c.includes('curtainwall') || c.includes('curtain'))
        return { icon: 'curtainWall', color: WALL_COLOR };
    if (c.includes('wall')) return { icon: 'wall', color: WALL_COLOR };

    // Horizontals
    if (c.includes('slab')) return { icon: 'slab', color: FLAT_COLOR };
    if (c.includes('roof')) return { icon: 'roof', color: FLAT_COLOR };
    if (c.includes('plate')) return { icon: 'plate', color: FLAT_COLOR };

    // Structural members
    if (c.includes('column')) return { icon: 'column', color: STRUCTURAL_COLOR };
    if (c.includes('beam')) return { icon: 'beam', color: STRUCTURAL_COLOR };
    if (c.includes('footing')) return { icon: 'footing', color: STRUCTURAL_COLOR };
    if (c.includes('pile')) return { icon: 'pile', color: STRUCTURAL_COLOR };
    if (c.includes('member')) return { icon: 'member', color: STRUCTURAL_COLOR };
    if (c.includes('tendon') || c.includes('reinforcing'))
        return { icon: 'tendon', color: STRUCTURAL_COLOR };

    // Circulation
    if (c.includes('stair')) return { icon: 'stair', color: CIRCULATION_COLOR };
    if (c.includes('ramp')) return { icon: 'ramp', color: CIRCULATION_COLOR };
    if (c.includes('railing')) return { icon: 'railing', color: CIRCULATION_COLOR };

    // MEP
    if (c.includes('duct')) return { icon: 'duct', color: MEP_COLOR };
    if (c.includes('pipe')) return { icon: 'pipe', color: MEP_COLOR };
    if (c.includes('cable') || c.includes('wire')) return { icon: 'cable', color: MEP_COLOR };
    if (c.includes('valve')) return { icon: 'valve', color: MEP_COLOR };
    if (c.includes('sanitary') || c.includes('terminal'))
        return { icon: 'sanitary', color: MEP_COLOR };
    if (c.includes('lamp') || c.includes('lighting'))
        return { icon: 'lamp', color: ELECTRIC_COLOR };
    if (c.includes('outlet') || c.includes('electric')) return { icon: 'outlet', color: MEP_COLOR };
    if (c.includes('fitting')) return { icon: 'fitting', color: MEP_COLOR };
    if (c.includes('flow')) return { icon: 'flow', color: MEP_COLOR };

    // Finishes & furnishings
    if (c.includes('covering')) return { icon: 'covering', color: FINISH_COLOR };
    if (c.includes('furnishing') || c.includes('furniture'))
        return { icon: 'furnishing', color: FINISH_COLOR };

    // Assemblies
    if (c.includes('assembly')) return { icon: 'assembly', color: ASSEMBLY_COLOR };
    if (c.includes('buildingelement') || c.includes('builtelement'))
        return { icon: 'cube', color: ASSEMBLY_COLOR };

    return { icon: 'cube', color: DEFAULT_COLOR };
}

export function classIcon(ifcClass: string | null | undefined): string {
    return getIfcClassVisual(ifcClass).icon;
}

export function classColor(ifcClass: string | null | undefined): string {
    return getIfcClassVisual(ifcClass).color;
}
